import { Box, Paper, Stack, Typography, useTheme } from "@mui/material";
import { AlertTriangle, LayoutDashboard, RefreshCcw } from "lucide-react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";

import logo from "assets/icons/logo.svg";
import UltimateButton from "components/UltimateButton";
import { WithChildren } from "utils/types";

export const ErrorFallback = ({ resetErrorBoundary }: FallbackProps) => {
  const theme = useTheme();

  const handleGoToDashboard = () => {
    resetErrorBoundary();
  };

  return (
    <Stack
      direction="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      p={3}
      sx={{
        bgcolor: (t) =>
          (t.palette as any).brandBackground?.primary ?? "#FAF9F6",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 480,
          width: "100%",
          p: 4,
          borderRadius: 3,
          border: "1px solid",
          borderColor: (t) =>
            (t.palette as any).brandBorder?.primary ?? "#F0F1F5",
        }}
      >
        <Stack
          direction="column"
          alignItems="center"
          textAlign="center"
          spacing={3}
        >
          <Box component="img" src={logo} alt="Logo" sx={{ height: 40 }} />

          <Stack
            direction="row"
            alignItems="center"
            justifyContent="center"
            p={1.5}
            sx={{
              bgcolor: (t) =>
                (t.palette as any).brandDanger?.secondary ?? "#FEE9E8",
              borderRadius: "50%",
            }}
          >
            <AlertTriangle
              size={32}
              color={(theme.palette as any).brandDanger?.primary ?? "#EC1F3D"}
            />
          </Stack>

          <Typography variant="headingMD" fontWeight={600}>
            Something went wrong
          </Typography>

          <Typography
            variant="textMD"
            sx={{
              color: (t) =>
                (t.palette as any).brandText?.secondary ?? "#A2A8B6",
            }}
          >
            Our team has been notified. Until we resolve this, you can try
            refreshing or return to the dashboard.
          </Typography>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            sx={{
              width: "100%",
              justifyContent: "center",
              pt: 1,
            }}
          >
            <UltimateButton
              Icon={
                LayoutDashboard as React.ComponentType<{
                  size?: number;
                  className?: string;
                  style?: React.CSSProperties;
                }>
              }
              text="Go to dashboard"
              color="brand"
              onClick={handleGoToDashboard}
              sx={{ minWidth: 160, py: 1.25 }}
            />
            <UltimateButton
              Icon={
                RefreshCcw as React.ComponentType<{
                  size?: number;
                  className?: string;
                  style?: React.CSSProperties;
                }>
              }
              text="Refresh"
              color="brandDanger"
              onClick={() => window.location.reload()}
              sx={{ minWidth: 160, py: 1.25 }}
            />
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );
};

const GlobalErrorBoundary = ({ children }: WithChildren) => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onError={(error, info) => {
        console.error({ error, info });
      }}
      onReset={() => {
        window.location.pathname = "/dashboard";
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default GlobalErrorBoundary;
